import React, { useEffect, useState } from 'react';
import GenericTable from '../../../shared/GenericTable';
import { useGetReportQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { Period } from '../../../enums/reports';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment-timezone';
import {
    Box,
    Chip,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import ContractorJobPreview from '../../Contractors/Jobs/ContractorJobPreview';
import { Job } from '../../../../app/Slices/SevenDayApiTypes';

const ScheduledJobs = () => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [searchParams, setSearchParams] = useSearchParams();

    const period = searchParams.get('period');
    const [selectedJob, setSelectedJob] = useState<number | null>(null);
    const [jobOpen, setJobOpen] = useState(false);

    const { data, isLoading } = useGetReportQuery(
        `/dashboard/scheduled-jobs?period=${period}`,
        { skip: !period },
    );

    useEffect(() => {
        if (!period) setSearchParams({ period: Period.CurrentWeek });

        const id = searchParams.get('selectedJob');

        if (id) {
            setSelectedJob(parseInt(id));
            setJobOpen(true);
        }
    }, [searchParams]);

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'moduleName',
            header: 'Experience',
        },
        {
            accessorKey: 'jobDate',
            header: 'Date',
            size: 120,
            filterVariant: 'date',
            filterFn: 'greaterThanOrEqualTo',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'll',
            },
            Cell: ({ cell }) => moment(cell.getValue<Date>()).format('ll'),
        },
        {
            accessorKey: 'id',
            header: 'Ref',
            Cell: ({ cell }) => {
                return `#${cell.getValue()}`;
            },
        },
        {
            accessorKey: 'services',
            header: 'Services',
            Cell: ({ cell }) => {
                const value = cell.getValue<any[]>();

                const remainders = isMobile ? value.length : value.length - 2;

                return (
                    <Box sx={{ display: 'flex', overflow: 'clip', gap: 0.5 }}>
                        {!isMobile &&
                            value
                                ?.slice(0, 2)
                                .map((service: string, index: number) => (
                                    <CustomChip
                                        key={index}
                                        variant='filled'
                                        sx={{
                                            backgroundColor: 'lightgrey',
                                        }}
                                        label={camelCaseToTitleCase(service)}
                                    />
                                ))}
                        {remainders > 0 && (
                            <Chip
                                sx={{ height: 'auto', fontSize: '10px' }}
                                label={`+ ${remainders}`}
                            />
                        )}
                    </Box>
                );
            },
        },

        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();

                return (
                    <CustomChip
                        variant='outlined'
                        color={
                            value === 'pending'
                                ? 'blue'
                                : value === 'declined'
                                  ? 'red'
                                  : value === 'accepted'
                                    ? 'yellow'
                                    : value === 'completed'
                                      ? 'green'
                                      : 'default'
                        }
                        label={value.toUpperCase()}
                    />
                );
            },
        },
    ];

    const handleView = (job: Job) => {
        setSelectedJob(job.id);
        setJobOpen(true);
    };

    const handleClose = () => {
        setJobOpen(false);
        setSelectedJob(null);
    };

    const handleChange = (e: SelectChangeEvent<string | null>) => {
        if (!e.target.value) return;

        setSearchParams({ period: e.target.value });
    };

    return (
        <>
            <Typography variant='h1'>Scheduled Jobs</Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    pt: '30px',
                    py: 1,
                }}
            >
                <Select value={period} onChange={handleChange} size='small'>
                    <MenuItem value={Period.Today}>Today</MenuItem>
                    <MenuItem value={Period.Tomorrow}>Tomorrow</MenuItem>
                    <MenuItem value={Period.CurrentWeek}>This Week</MenuItem>
                    <MenuItem value={Period.PreviousWeek}>Last Week</MenuItem>
                    <MenuItem value={Period.NextWeek}>Next Week</MenuItem>
                    <MenuItem value={Period.CurrentMonth}>This Month</MenuItem>
                    <MenuItem value={Period.CurrentYear}>This Year</MenuItem>
                </Select>
            </Box>
            <GenericTable
                tableName='scheduled-jobs'
                columns={columns}
                data={data ?? []}
                loading={isLoading}
                enableRowSelection={false}
                enableMultiRowSelection={false}
                onRowClick={(row) => handleView(row)}
            />
            <ContractorJobPreview
                id={selectedJob}
                open={jobOpen}
                onClose={handleClose}
            />
        </>
    );
};

export default ScheduledJobs;
